(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.ArtCards = api;
})(typeof self !== 'undefined' ? self : this, function () {
  const RULES = { maxDesigns: 5, minPerDesignMulti: 40, minSingle: 2 };
  const QUALITY_MIN_DPI = 150;
  const LOGO_MIN_DPI = 100;
  const PACKAGING = ['Cards Only (Free Envelopes)', 'Clear Plastic Sleeves'];

  const BACK_FONTS = ['Playfair Display', 'Montserrat', 'Lora', 'Raleway', 'Cormorant Garamond'];
  const BACK_SIZES = {
    line1: [12, 14, 16, 18, 20, 22, 24],
    line2: [10, 11, 12, 13, 14, 16, 18],
    line3: [9, 10, 11, 12, 13, 14]
  };
  const BACK_DEFAULTS = {
    line1_font: 'Playfair Display', line1_size: 16, line1_style: 'bold',
    line2_font: 'Lora', line2_size: 13, line2_style: 'italic',
    line3_font: 'Montserrat', line3_size: 11, line3_style: 'normal',
    position: 'bottom'
  };
  const BACK_KEYS = [
    'line1_text', 'line1_font', 'line1_size', 'line1_style',
    'line2_text', 'line2_font', 'line2_size', 'line2_style',
    'line3_text', 'line3_font', 'line3_size', 'line3_style',
    'position'
  ];
  const BACK_SIZE_KEYS = ['line1_size', 'line2_size', 'line3_size'];

  const LOGO_WIDTHS = { small: 18, medium: 30, large: 44 };
  const LOGO_EDGE_PCT = 8;
  const LOGO_GAP_PCT = 8;
  const LOGO_PAD_PX = 3;

  const PRINT_DPI_STEPS = [600, 450, 300];

  const FIT_MODES = ['crop', 'fit', 'border'];
  const STORAGE_PREFIX = 'https://storage.googleapis.com/';
  const SEPARATOR = '───────────────';
  const SAME_BACK = 'Same for all designs';

  function sizeFor(cardSizeSetting, handle){
    const isSquare = cardSizeSetting === '5.25x5.25' ||
      (cardSizeSetting !== '5x7' && String(handle || '').includes('square'));
    if(isSquare) return { label: '5.25 × 5.25"', isSquare: true, ratio: 1, shortIn: 5.25, longIn: 5.25 };
    return { label: '5 × 7"', isSquare: false, ratio: 5 / 7, shortIn: 5, longIn: 7 };
  }

  function emptyBack(){
    return {
      line1_text: '', line1_font: BACK_DEFAULTS.line1_font, line1_size: BACK_DEFAULTS.line1_size, line1_style: BACK_DEFAULTS.line1_style,
      line2_text: '', line2_font: BACK_DEFAULTS.line2_font, line2_size: BACK_DEFAULTS.line2_size, line2_style: BACK_DEFAULTS.line2_style,
      line3_text: '', line3_font: BACK_DEFAULTS.line3_font, line3_size: BACK_DEFAULTS.line3_size, line3_style: BACK_DEFAULTS.line3_style,
      position: BACK_DEFAULTS.position
    };
  }

  function designBack(sharedBack, design){
    if(design.ownBack) return Object.assign({}, design.ownBack);
    const back = Object.assign({}, sharedBack);
    back.line2_text = String(design.title || '').trim();
    return back;
  }

  function backJson(back){
    const ordered = {};
    BACK_KEYS.forEach(key => {
      ordered[key] = BACK_SIZE_KEYS.includes(key) ? parseInt(back[key], 10) : back[key];
    });
    return JSON.stringify(ordered);
  }

  function backText(back){
    return [back.line1_text, back.line2_text, back.line3_text].filter(Boolean).join(' · ');
  }

  function lineStyle(font, size, style){
    return font + ', ' + size + 'px, ' + style;
  }

  function positionLabel(position){
    const pos = position || 'bottom';
    return pos.charAt(0).toUpperCase() + pos.slice(1);
  }

  function logoInset({ logoPosition, textPosition, textPct }){
    if(logoPosition === 'center') return textPosition === 'top' ? 62 : (textPosition === 'bottom' ? 40 : 50);

    const sameEdge = (logoPosition === 'bottom' && textPosition === 'bottom') ||
                     (logoPosition === 'top' && textPosition === 'top');
    if(sameEdge){
      const pct = textPct != null ? textPct : 18;
      const TEXT_EDGE = 12, GAP = 5;
      return Math.max(LOGO_EDGE_PCT, Math.min(70, TEXT_EDGE + pct + GAP));
    }
    return LOGO_EDGE_PCT;
  }

  function logoMeta({ position, size, textPosition, textPct }){
    return {
      position: position,
      size: size,
      width_pct: LOGO_WIDTHS[size] || 30,
      inset_pct: logoInset({ logoPosition: position, textPosition, textPct }),
      gap_pct: LOGO_GAP_PCT,
      padding_px: LOGO_PAD_PX,
      relative_to_text: textPosition || 'bottom'
    };
  }

  function logoPlacementText(layout){
    const lay = layout || {};
    return lay.inset_pct != null
      ? 'inset ' + lay.inset_pct + '% · width ' + (lay.width_pct != null ? lay.width_pct : '?') + '% · gap ' + (lay.gap_pct != null ? lay.gap_pct : '?') + '%'
      : '';
  }

  function printPixelSize({ isSquare, orientation, dpi }){
    let inW, inH;
    if(isSquare){ inW = 5.25; inH = 5.25; }
    else if(orientation !== 'landscape'){ inW = 5; inH = 7; }
    else { inW = 7; inH = 5; }
    return { width: Math.round(inW * dpi), height: Math.round(inH * dpi) };
  }

  function backSignature(design){
    const logo = design.logo;
    return JSON.stringify(design.back || {}) + '|' + ((logo && logo.key) || '') + '|' +
      ((logo && logo.position) || '') + '|' + ((logo && logo.size) || '') + '|' + (design.orientation || '');
  }

  function checkDesigns(designs){
    if(!designs.length || designs.length > RULES.maxDesigns){
      throw new Error('A pack needs 1 to ' + RULES.maxDesigns + ' designs, got ' + designs.length);
    }
    designs.forEach((d, i) => {
      const n = i + 1;
      if(!d.name) throw new Error('Design ' + n + ' has no name');
      if(typeof d.url !== 'string' || !d.url.startsWith(STORAGE_PREFIX) || d.url.includes('?')){
        throw new Error('Design ' + n + ' url is not a permanent storage address: ' + d.url);
      }
      if(!FIT_MODES.includes(d.fitMode)) throw new Error('Design ' + n + ' has an unknown fit mode: ' + d.fitMode);
    });
  }

  function addDesignProperties(props, pack, d, n){
    if(d.note) props['Design ' + n + ' — Note'] = d.note;
    props['_image_' + n + '_url'] = d.url;
    props['_image_' + n + '_name'] = d.name;
    props['_image_' + n + '_qty'] = String(d.alloc);
    props['_image_' + n + '_customer_id'] = pack.customerId;
    props['_image_' + n + '_session_id'] = pack.sessionId;
    props['_image_' + n + '_orientation'] = d.orientation || 'portrait';
    props['_image_' + n + '_fit_mode'] = d.fitMode;
    if(d.back) props['_image_' + n + '_back_json'] = backJson(d.back);

    const orientTxt = pack.isSquare ? '' : (', ' + (d.orientation === 'landscape' ? 'Landscape' : 'Portrait'));
    props['Design ' + n + ' — Front'] = d.name + ' (' + d.alloc + ' cards' + orientTxt + ')';

    if(d.back){
      const b = d.back;
      const text = backText(b);
      if(text) props['Design ' + n + ' — Back Text'] = text;
      props['Design ' + n + ' — Back Position'] = positionLabel(b.position);
      if(b.line1_text) props['Design ' + n + ' — Line 1 Style'] = lineStyle(b.line1_font, b.line1_size, b.line1_style);
      if(b.line2_text) props['Design ' + n + ' — Line 2 Style'] = lineStyle(b.line2_font, b.line2_size, b.line2_style);
      if(b.line3_text) props['Design ' + n + ' — Line 3 Style'] = lineStyle(b.line3_font, b.line3_size, b.line3_style);
    }
  }

  function addLogoProperties(props, designs){
    const d0 = designs[0];
    const allHaveLogo = designs.length > 1 && designs.every(d => d.logo);
    const sharedLogo = allHaveLogo && designs.every(d =>
      d.logo.key === d0.logo.key &&
      d.logo.position === d0.logo.position &&
      d.logo.size === d0.logo.size);

    if(sharedLogo){
      const lay = d0.logo.layout || {}, lpos = d0.logo.position || 'bottom';
      props['_logo_url'] = d0.logo.url || '';
      props['_logo_position'] = lpos;
      props['_logo_size'] = d0.logo.size || 'medium';
      props['_logo_layout'] = JSON.stringify(lay);
      props['Logo (all designs)'] = positionLabel(lpos) + ' · ' + (d0.logo.size || 'medium');
      if(logoPlacementText(lay)) props['Logo Placement'] = logoPlacementText(lay);
    } else {
      designs.forEach((d, i) => {
        if(!d.logo) return;
        const n = i + 1, lay = d.logo.layout || {}, lpos = d.logo.position || 'bottom';
        props['_image_' + n + '_logo_url'] = d.logo.url || '';
        props['_image_' + n + '_logo_position'] = lpos;
        props['_image_' + n + '_logo_size'] = d.logo.size || 'medium';
        props['_image_' + n + '_logo_layout'] = JSON.stringify(lay);
        props['Design ' + n + ' — Logo'] = positionLabel(lpos) + ' · ' + (d.logo.size || 'medium');
        if(logoPlacementText(lay)) props['Design ' + n + ' — Logo Placement'] = logoPlacementText(lay);
      });
    }
  }

  function addBackFileProperties(props, designs){
    const d0 = designs[0], sig0 = backSignature(d0);
    const sharedBack = designs.length > 1 && designs.every(d => backSignature(d) === sig0);

    if(sharedBack && d0.backPreviewUrl){
      props['_back_preview_url'] = d0.backPreviewUrl;
      props['Back Style'] = SAME_BACK;
    } else {
      designs.forEach((d, i) => {
        if(!d.backPreviewUrl) return;
        props['_image_' + (i + 1) + '_back_preview_url'] = d.backPreviewUrl;
      });
      const allBacks = designs.filter(d => d.back).map(d => backJson(d.back));
      if(allBacks.length > 1 && allBacks.every(b => b === allBacks[0])) props['Back Style'] = SAME_BACK;
    }

    if(sharedBack && d0.backPrintUrl){
      props['_back_print_url'] = d0.backPrintUrl;
    } else {
      designs.forEach((d, i) => {
        if(!d.backPrintUrl) return;
        props['_image_' + (i + 1) + '_back_print_url'] = d.backPrintUrl;
      });
    }
  }

  function buildProperties(pack){
    const designs = pack.designs || [];
    checkDesigns(designs);

    const props = {};
    props['Card Size'] = pack.sizeLabel;
    props['Total Cards'] = String(pack.quantity);
    if(pack.rush) props['Rush request'] = 'Yes';
    props['  '] = SEPARATOR;

    designs.forEach((d, i) => {
      addDesignProperties(props, pack, d, i + 1);
      if(i < designs.length - 1) props['  '.repeat(i + 2)] = SEPARATOR;
    });

    addLogoProperties(props, designs);
    addBackFileProperties(props, designs);

    props['_design_count'] = String(designs.length);
    props['_customer_id'] = String(pack.customerId);
    props['_session_id'] = pack.sessionId;
    if(pack.draftId) props['_draft_id'] = pack.draftId;
    return props;
  }

  function loadImg(src){
    return new Promise((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = src; });
  }

  async function renderBackPreview(d, options){
    const b = d.backData;
    const hasText = b && (b.line1_text || b.line2_text || b.line3_text);
    if(!hasText && !d.logoDataUrl) return '';

    try { if(document.fonts && document.fonts.ready) await document.fonts.ready; } catch(e){}

    const portrait = d.orientation !== 'landscape';
    let w, h;
    if(options.isSquare){ w = 300; h = 300; }
    else if(portrait){ w = 260; h = 364; }
    else { w = 364; h = 260; }

    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = '#e0ddd7';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, w - 1, h - 1);

    const pos = (b && b.position) || 'bottom';

    if(d.logoDataUrl){
      try {
        const lay = d.logoLayout || {};
        const widthPct = lay.width_pct != null ? lay.width_pct : 30;
        const insetPct = lay.inset_pct != null ? lay.inset_pct : 8;
        const limg = await loadImg(d.logoDataUrl);
        const lw = w * widthPct / 100;
        const lh = lw * (limg.naturalHeight / limg.naturalWidth || 1);
        const lp = d.logoPosition || 'bottom';
        let lx, ly;
        if(lp === 'top'){ lx = w / 2 - lw / 2; ly = h * insetPct / 100; }
        else if(lp === 'bottom'){ lx = w / 2 - lw / 2; ly = h - h * insetPct / 100 - lh; }
        else if(lp === 'left'){ lx = w * insetPct / 100; ly = h / 2 - lh / 2; }
        else if(lp === 'right'){ lx = w - w * insetPct / 100 - lw; ly = h / 2 - lh / 2; }
        else { lx = w / 2 - lw / 2; ly = h * insetPct / 100 - lh / 2; }
        ctx.drawImage(limg, lx, ly, lw, lh);
      } catch(e){}
    }

    if(hasText){
      const scale = (Math.min(w, h) / 240) * 0.62;
      const lines = [];
      if(b.line1_text) lines.push({t: b.line1_text, f: b.line1_font, s: b.line1_size, st: b.line1_style});
      if(b.line2_text) lines.push({t: b.line2_text, f: b.line2_font, s: b.line2_size, st: b.line2_style});
      if(b.line3_text) lines.push({t: b.line3_text, f: b.line3_font, s: b.line3_size, st: b.line3_style});
      const gap = 3;
      const lineH = lines.map(l => Math.max(8, Math.round(l.s * scale)) * 1.42);
      const blockH = lineH.reduce((a, c) => a + c, 0) + gap * (lines.length - 1);

      let startY;
      if(pos === 'top') startY = h * 0.12;
      else if(pos === 'bottom') startY = h * 0.88 - blockH;
      else startY = h / 2 - blockH / 2;

      let align, x;
      if(pos === 'left'){ align = 'left'; x = w * 0.08; }
      else if(pos === 'right'){ align = 'right'; x = w * 0.92; }
      else { align = 'center'; x = w / 2; }

      ctx.textAlign = align;
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#222';
      let cy = startY;
      lines.forEach((l, i) => {
        const fp = Math.max(8, Math.round(l.s * scale));
        const italic = l.st === 'italic' ? 'italic ' : '';
        const weight = l.st === 'bold' ? '700' : '400';
        ctx.font = italic + weight + ' ' + fp + 'px "' + l.f + '", serif';
        ctx.fillText(l.t, x, cy + lineH[i] / 2);
        cy += lineH[i] + gap;
      });
    }

    let url = canvas.toDataURL('image/jpeg', 0.72);
    if(url.length > 60000) url = canvas.toDataURL('image/jpeg', 0.55);
    return url;
  }

  async function renderBackPrint(d, dpi, options){
    dpi = dpi || 600;
    const b = d.backData;
    const hasText = b && (b.line1_text || b.line2_text || b.line3_text);
    if(!hasText && !d.logoDataUrl) return null;

    try { if(document.fonts && document.fonts.ready) await document.fonts.ready; } catch(e){}

    const portrait = d.orientation !== 'landscape';
    let inW, inH;
    if(options.isSquare){ inW = 5.25; inH = 5.25; }
    else if(portrait){ inW = 5; inH = 7; }
    else { inW = 7; inH = 5; }
    const w = Math.round(inW * dpi);
    const h = Math.round(inH * dpi);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if(!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    if('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);

    const pos = (b && b.position) || 'bottom';

    if(d.logoDataUrl){
      try {
        const lay = d.logoLayout || {};
        const widthPct = lay.width_pct != null ? lay.width_pct : 30;
        const insetPct = lay.inset_pct != null ? lay.inset_pct : 8;
        const limg = await loadImg(d.logoDataUrl);
        try { if(limg.decode) await limg.decode(); } catch(e){}
        ctx.imageSmoothingEnabled = true;
        if('imageSmoothingQuality' in ctx) ctx.imageSmoothingQuality = 'high';
        const lw = w * widthPct / 100;
        const lh = lw * (limg.naturalHeight / limg.naturalWidth || 1);
        const lp = d.logoPosition || 'bottom';
        let lx, ly;
        if(lp === 'top'){ lx = w / 2 - lw / 2; ly = h * insetPct / 100; }
        else if(lp === 'bottom'){ lx = w / 2 - lw / 2; ly = h - h * insetPct / 100 - lh; }
        else if(lp === 'left'){ lx = w * insetPct / 100; ly = h / 2 - lh / 2; }
        else if(lp === 'right'){ lx = w - w * insetPct / 100 - lw; ly = h / 2 - lh / 2; }
        else { lx = w / 2 - lw / 2; ly = h * insetPct / 100 - lh / 2; }
        ctx.drawImage(limg, lx, ly, lw, lh);
      } catch(e){}
    }

    if(hasText){
      const scale = (Math.min(w, h) / 240) * 0.62;
      const lines = [];
      if(b.line1_text) lines.push({t: b.line1_text, f: b.line1_font, s: b.line1_size, st: b.line1_style});
      if(b.line2_text) lines.push({t: b.line2_text, f: b.line2_font, s: b.line2_size, st: b.line2_style});
      if(b.line3_text) lines.push({t: b.line3_text, f: b.line3_font, s: b.line3_size, st: b.line3_style});
      const gap = Math.round(Math.min(w, h) * 0.0115);
      const lineH = lines.map(l => Math.max(8, Math.round(l.s * scale)) * 1.42);
      const blockH = lineH.reduce((a, c) => a + c, 0) + gap * (lines.length - 1);

      for(const l of lines){
        const fp = Math.max(8, Math.round(l.s * scale));
        const italic = l.st === 'italic' ? 'italic ' : '';
        const weight = l.st === 'bold' ? '700' : '400';
        l._font = italic + weight + ' ' + fp + 'px "' + l.f + '", serif';
        try { if(document.fonts && document.fonts.load) await document.fonts.load(l._font); } catch(e){}
      }

      let startY;
      if(pos === 'top') startY = h * 0.12;
      else if(pos === 'bottom') startY = h * 0.88 - blockH;
      else startY = h / 2 - blockH / 2;

      let align, x;
      if(pos === 'left'){ align = 'left'; x = w * 0.08; }
      else if(pos === 'right'){ align = 'right'; x = w * 0.92; }
      else { align = 'center'; x = w / 2; }

      ctx.textAlign = align;
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#222';
      let cy = startY;
      lines.forEach((l, i) => {
        ctx.font = l._font;
        ctx.fillText(l.t, x, cy + lineH[i] / 2);
        cy += lineH[i] + gap;
      });
    }

    const blob = await new Promise(res => { canvas.toBlob(res, 'image/png'); });
    try { canvas.width = canvas.height = 0; } catch(e){}
    return blob;
  }

  return {
    RULES,
    QUALITY_MIN_DPI,
    LOGO_MIN_DPI,
    PACKAGING,
    sizeFor,
    BACK_FONTS,
    BACK_SIZES,
    BACK_DEFAULTS,
    LOGO_WIDTHS,
    LOGO_EDGE_PCT,
    LOGO_GAP_PCT,
    LOGO_PAD_PX,
    emptyBack,
    designBack,
    backJson,
    backText,
    lineStyle,
    positionLabel,
    logoInset,
    logoMeta,
    logoPlacementText,
    printPixelSize,
    PRINT_DPI_STEPS,
    backSignature,
    buildProperties,
    renderBackPreview,
    renderBackPrint
  };
});
